import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { transactionApi } from "@bw/shared/api";
import {
  assertPaymentMethodDetachBarrierGenerationCurrent,
  beginPaymentMethodRemoval,
  capturePaymentMethodDetachBarrierGeneration,
  completePaymentMethodRemoval,
  finishPaymentMethodRemoval,
  invalidatePaymentMethodRemovalQueries,
  isDefinitivePaymentMethodRemovalFailure,
  isPaymentMethodRemovalOperationCurrentForQuery,
  isPaymentMethodDetachBarrierGenerationCurrent,
  isSamePaymentMethodIdentity,
  PaymentMethodRemovalInProgressError,
  PaymentMethodRemovalPersistenceError,
  PaymentMethodRemovalReferenceError,
  reconcilePaymentMethods,
  rejectPaymentMethodRemoval,
} from "@bw/shared/hooks";
import type { SavedPaymentMethodResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * The caller's CROSS-ORG saved cards (GET /users/me/payment-methods, #1392),
 * SERVER-SCOPED to the auth user — the page never passes its own uid. The server
 * aggregates the player's saved cards across EVERY org's connected account; each
 * row carries a composite `id = "{connect_account_id}:{payment_method_id}"` (the
 * delete key) plus the owning `org_id`/`org_name`. The page renders the server's
 * data verbatim and does NOT re-sort or derive state.
 */
export function useSavedPaymentMethods() {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.me.paymentMethods();
  return useQuery<SavedPaymentMethodResponse[]>({
    queryKey,
    queryFn: async () => {
      const generation = capturePaymentMethodDetachBarrierGeneration();
      const methods = await transactionApi.listMyPaymentMethods();
      assertPaymentMethodDetachBarrierGenerationCurrent(generation);
      return reconcilePaymentMethods(
        queryClient,
        queryKey,
        methods,
        generation,
      );
    },
  });
}

/**
 * Remove a saved card (DELETE /users/me/payment-methods/{compositeId}). Keyed by
 * the composite `id` from the list row. Before dispatch, every known alias of
 * the organization wallet is fail-closed and a same-tab reload fence is stored.
 * A definitive rejection restores the source row; success records monotonic
 * detach truth and refetches every alias. The page retains a disabled display
 * snapshot while canonical charge data stays filtered.
 */
export function useRemovePaymentMethod() {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.me.paymentMethods();
  return useMutation<void, Error, SavedPaymentMethodResponse>({
    mutationFn: async (card) => {
      const generation = capturePaymentMethodDetachBarrierGeneration();
      const targetMethods = [card];
      const paymentMethodId = card.payment_method_id;
      const compositeId = card.id;
      if (typeof compositeId !== "string" || compositeId.length === 0) {
        throw new PaymentMethodRemovalReferenceError();
      }
      const priorMethods =
        queryClient.getQueryData<SavedPaymentMethodResponse[]>(queryKey) ?? [];
      const priorIndex = Math.max(
        0,
        priorMethods.findIndex((method) =>
          isSamePaymentMethodIdentity(method, card),
        ),
      );
      const operation = beginPaymentMethodRemoval(
        queryClient,
        queryKey,
        targetMethods,
        paymentMethodId,
        generation,
        false,
      );
      if (operation === null) {
        throw new PaymentMethodRemovalInProgressError();
      }
      try {
        await transactionApi.removeMyPaymentMethod(compositeId);
        assertPaymentMethodDetachBarrierGenerationCurrent(generation);
        try {
          completePaymentMethodRemoval(queryClient, operation);
        } catch (error) {
          if (!(error instanceof PaymentMethodRemovalPersistenceError)) {
            throw error;
          }
          // A 2xx DELETE is authoritative. Retain the durable pre-dispatch
          // uncertain fence instead of restoring/retrying a detached card.
        }
        await invalidatePaymentMethodRemovalQueries(
          queryClient,
          operation,
        ).catch(() => undefined);
      } catch (error) {
        if (
          isPaymentMethodDetachBarrierGenerationCurrent(generation) &&
          isDefinitivePaymentMethodRemovalFailure(error)
        ) {
          const fenceCleared = rejectPaymentMethodRemoval(
            queryClient,
            operation,
          );
          if (
            fenceCleared &&
            isPaymentMethodRemovalOperationCurrentForQuery(operation) &&
            queryClient.getQueryState(queryKey)?.status !== "error"
          ) {
            queryClient.setQueryData<SavedPaymentMethodResponse[]>(
              queryKey,
              (old) => {
                if (
                  old?.some((method) =>
                    isSamePaymentMethodIdentity(method, card),
                  )
                ) {
                  return old;
                }
                const restored = [...(old ?? [])];
                restored.splice(Math.min(priorIndex, restored.length), 0, card);
                return restored;
              },
            );
          }
          void invalidatePaymentMethodRemovalQueries(
            queryClient,
            operation,
          ).catch(() => undefined);
        }
        throw error;
      } finally {
        if (isPaymentMethodDetachBarrierGenerationCurrent(generation)) {
          finishPaymentMethodRemoval(queryClient, operation);
        }
      }
    },
  });
}
